import { useEffect, useRef, useState } from "react";
import { type ContextMenuItem, isOpenableSubmenu } from "./items";
import type { InitialFocus } from "./useInitialFocus";

/** How long the pointer must rest on an item before its submenu opens (or the open one closes). */
const HOVER_DELAY_MS = 120;

export interface OpenSubmenu {
  /** Position of the submenu item in the panel's items. */
  index: number;
  initialFocus: InitialFocus;
}

export interface Submenu {
  openSub: OpenSubmenu | null;
  open: (index: number, initialFocus: InitialFocus) => void;
  close: () => void;
  /** The pointer entered the item at `index`: after a short delay, open its submenu or close the open one. */
  hover: (index: number, item: ContextMenuItem) => void;
  cancelHover: () => void;
}

/** Which submenu of a panel is open, if any. */
export function useSubmenu(): Submenu {
  const [openSub, setOpenSub] = useState<OpenSubmenu | null>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  const cancelHover = (): void => clearTimeout(hoverTimer.current);

  function hover(index: number, item: ContextMenuItem): void {
    cancelHover();
    if (openSub?.index === index) return;
    const next: OpenSubmenu | null = isOpenableSubmenu(item) ? { index, initialFocus: "keep" } : null;
    hoverTimer.current = setTimeout(() => setOpenSub(next), HOVER_DELAY_MS);
  }

  return {
    openSub,
    open: (index, initialFocus) => setOpenSub({ index, initialFocus }),
    close: () => setOpenSub(null),
    hover,
    cancelHover,
  };
}
